/**
 * Client-side image preparation.
 *
 * A photograph straight off a phone is 3-6 MB, 4000px tall and portrait, while
 * every place a treatment photo is shown is landscape - so something always has
 * to be thrown away. Choosing what, here in the browser, means the studio picks
 * the part of the picture that matters (see ImageCropper) instead of the CSS
 * silently keeping the middle, the API is not handling multi-megabyte bodies,
 * and Blob Storage holds something sensibly sized - all without a native image
 * library on the server.
 *
 * Two steps, because a crop needs a person in between: loadCropSource decodes
 * and normalises the chosen file, then renderCrop cuts the rectangle they chose
 * out of it.
 */

/**
 * The shape every treatment photo is stored in - the 16:10 of the booking
 * service card, the largest place a photograph appears.
 */
export const CROP_ASPECT = 16 / 10;

/**
 * Filled in behind a photograph that does not cover the whole frame - the studio
 * can zoom out past the edges of a picture to keep all of it, and the bars that
 * leaves are baked into the stored image. The page ground (--sand-50), so they
 * disappear into the page wherever the photograph is shown.
 */
export const CROP_PAD_COLOUR = '#faf7f2';

/** Stored size. Comfortably above the largest rendered size. */
export const OUTPUT_WIDTH = 1600;
export const OUTPUT_HEIGHT = Math.round(OUTPUT_WIDTH / CROP_ASPECT);

/**
 * Longest edge of the working copy the cropper pans around. Enough detail left
 * to zoom into a corner, small enough to hold in memory comfortably.
 */
const SOURCE_MAX_EDGE = 2000;
const JPEG_QUALITY = 0.85;

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];

/** A rectangle in the coordinates of a CropSource. */
export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * A decoded, orientation-corrected copy of the chosen file, ready to be cropped.
 *
 * The canvas is what the final crop is drawn from and previewUrl shows the very
 * same pixels, so the cropper cannot disagree with the result. Call release when
 * finished with it.
 */
export interface CropSource {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  previewUrl: string;
  contentType: string;
  originalBytes: number;
  release: () => void;
}

export interface PreparedImage {
  blob: Blob;
  contentType: string;
  width: number;
  height: number;
  originalBytes: number;
}

export class ImagePrepError extends Error {}

function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap is far faster, but is absent on older Safari, so fall back
  // to an <img>. Both are asked to apply EXIF orientation - an <img> does so by
  // default, and browsers have disagreed about the bitmap default.
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() =>
      loadViaImgElement(file),
    );
  }
  return loadViaImgElement(file);
}

function loadViaImgElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new ImagePrepError('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, contentType: string): Promise<Blob> {
  const quality = contentType === 'image/png' ? undefined : JPEG_QUALITY;
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ImagePrepError('That image could not be processed.'))),
      contentType,
      quality,
    ),
  );
}

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');
  if (!context) throw new ImagePrepError('This browser cannot process images.');
  context.imageSmoothingQuality = 'high';
  return context;
}

/**
 * Decodes a chosen file into a working copy the cropper can pan around.
 *
 * Transparency is preserved by keeping PNG as PNG; everything else becomes
 * JPEG, which is smaller for photographs.
 */
export async function loadCropSource(file: File): Promise<CropSource> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new ImagePrepError('Choose a JPEG, PNG, WebP or AVIF image.');
  }

  const source = await loadBitmap(file);
  const sourceWidth = 'width' in source ? source.width : 0;
  const sourceHeight = 'height' in source ? source.height : 0;
  if (!sourceWidth || !sourceHeight) {
    throw new ImagePrepError('That image appears to be empty.');
  }

  const scale = Math.min(1, SOURCE_MAX_EDGE / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  context2d(canvas).drawImage(source as CanvasImageSource, 0, 0, width, height);
  if ('close' in source) source.close();

  const contentType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const previewUrl = URL.createObjectURL(await toBlob(canvas, contentType));

  return {
    canvas,
    width,
    height,
    previewUrl,
    contentType,
    originalBytes: file.size,
    release: () => {
      URL.revokeObjectURL(previewUrl);
      // Shrinking the canvas releases its backing store rather than waiting for
      // a collection that may be a while coming.
      canvas.width = 0;
      canvas.height = 0;
    },
  };
}

/** Cuts the chosen rectangle out of a working copy, at the stored size. */
export async function renderCrop(source: CropSource, rect: CropRect): Promise<PreparedImage> {
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  const context = context2d(canvas);

  // Ground for anything the photograph does not cover: the bars left by zooming
  // out past its edges, and the alpha of a transparent WebP, which JPEG cannot
  // carry and would otherwise turn black. PNG keeps its transparency instead.
  if (source.contentType !== 'image/png') {
    context.fillStyle = CROP_PAD_COLOUR;
    context.fillRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
  }

  // A rect reaching outside the working copy is clipped by the canvas, along
  // with its share of the destination - so the ground above survives there.
  context.drawImage(
    source.canvas,
    rect.x,
    rect.y,
    rect.width,
    rect.height,
    0,
    0,
    OUTPUT_WIDTH,
    OUTPUT_HEIGHT,
  );

  return {
    blob: await toBlob(canvas, source.contentType),
    contentType: source.contentType,
    width: OUTPUT_WIDTH,
    height: OUTPUT_HEIGHT,
    originalBytes: source.originalBytes,
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
